import React, { useState } from "react";
import { createItem, deleteItem, updateItem } from "../../../api/generics";
import { getAbsoluteUrl } from "../../../api/urls";
import { fetchAuthenticated } from "../../../api/utils";
import Document from "../../../models/document";
import { SUCCESS } from "../../../utils/constants/tags";
import { successAlert, warningAlert } from "../../utils/messages";
import { ChangedCells } from "./useChangedCells";

interface UseDocumentPersistenceParams {
  additionalFields: any;
  changedCellsRef: React.MutableRefObject<ChangedCells>;
  companyId: number;
  documents: Document[];
  fetchDocuments: () => Promise<void>;
  gridApi: any;
  resetChangedCells: () => void;
  setDocuments: React.Dispatch<React.SetStateAction<Document[]>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const addAdditionalFieldNames = (documentData: any, additionalFields: any) => {
  for (let i = 1; i <= 10; i++) {
    if (documentData[`fieldValue${i}`] != null) {
      documentData[`fieldName${i}`] = additionalFields
        ? additionalFields[`fieldName${i}` as keyof typeof additionalFields]
        : undefined;
    }
  }
};

const appendDocumentUploadFields = (
  formData: FormData,
  file: File,
  documentData: Document,
  companyId: number,
) => {
  formData.append("name", documentData.name || file.name);
  formData.append("version", documentData.version || "1");
  formData.append("company_id", String(companyId));
  formData.append("file_path", file.name);
  formData.append("asset_id", String(documentData.assetId ?? ""));
  formData.append("document_type", documentData.documentType || "digital");
  formData.append("physical_copy_required", String(documentData.physicalCopyRequired || false));
  formData.append("expiration_notice_days", String(documentData.expirationNoticeDays || 30));
  formData.append("expiration_grace_days", String(documentData.expirationGraceDays || 15));
  formData.append("expiration_reminder_repeat_days", String(documentData.expirationReminderRepeatDays || 7));
  if (documentData.documentCategoryId != null)
    formData.append("document_category_id", String(documentData.documentCategoryId));
  if (documentData.documentSubcategoryId != null)
    formData.append("document_subcategory_id", String(documentData.documentSubcategoryId));
  if (documentData.subcompanyId != null)
    formData.append("subcompany_id", String(documentData.subcompanyId));
  if (documentData.expirationDate)
    formData.append("expiration_date", documentData.expirationDate);
};

const useDocumentPersistence = ({
  additionalFields,
  changedCellsRef,
  companyId,
  documents,
  fetchDocuments,
  gridApi,
  resetChangedCells,
  setDocuments,
  setLoading,
}: UseDocumentPersistenceParams) => {
  const [deletingIds, setDeletingIds] = useState<number[]>([]);

  const handleSaveChanges = async () => {
    gridApi?.stopEditing();
    await new Promise((resolve) => setTimeout(resolve, 0));
    const currentChangedCells = changedCellsRef.current;
    if (Object.keys(currentChangedCells).length === 0) {
      warningAlert("No hay cambios para guardar.");
      return;
    }
    try {
      setLoading(true);
      const savePromises: Promise<any>[] = [];
      const tempIdMap: { [key: number]: number } = {};

      gridApi.forEachNode((node: any) => {
        if (!currentChangedCells[node.data.id]) return;
        const docData = node.data;
        const { typeName, ...dataWithoutTypeName } = docData;
        addAdditionalFieldNames(dataWithoutTypeName, additionalFields);

        if (docData.id < 0) {
          const tempId = docData.id;
          const createData = { ...dataWithoutTypeName, companyId, id: undefined };
          savePromises.push(
            createItem<Document>("/documents/create/", createData).then((response) => {
              if (response.status !== SUCCESS)
                throw new Error(response.detail || "Failed to create document");
              if (response.data?.id !== undefined)
                tempIdMap[tempId] = response.data.id;
              return { success: true };
            }),
          );
          return;
        }
        const { id, ...dataToSend } = dataWithoutTypeName;
        savePromises.push(
          updateItem<Document>(`/documents/${id}/`, { ...dataToSend, companyId }).then(
            (response) => {
              if (response.status !== SUCCESS)
                throw new Error(response.detail || "Failed to update document");
              return { success: true };
            },
          ),
        );
      });

      const results = await Promise.all(savePromises);
      if (Object.keys(tempIdMap).length > 0) {
        setDocuments((prev) =>
          prev.map((doc) =>
            doc.id !== undefined && doc.id in tempIdMap
              ? { ...doc, id: tempIdMap[doc.id] }
              : doc,
          ),
        );
      }
      if (results.every((r) => r.success)) {
        successAlert("Cambios guardados con éxito!");
        resetChangedCells();
        fetchDocuments();
      }
    } catch (error) {
      console.error("Save operation failed:", error);
      warningAlert(error instanceof Error ? error.message : String(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      setDeletingIds((prev) => [...prev, id]);
      if (id < 0) {
        setDocuments((prev) => prev.filter((doc) => doc.id !== id));
        return;
      }
      const documentToDelete = documents.find((doc) => doc.id === id);
      if (!documentToDelete) {
        warningAlert("Documento no encontrado.");
        return;
      }
      const deleteStatus = await deleteItem<Document>(`/documents/${id}/`, documentToDelete);
      if (deleteStatus.status === SUCCESS) {
        successAlert("Documento eliminado con éxito!");
        setDocuments((prev) => prev.filter((doc) => doc.id !== id));
        fetchDocuments();
      } else {
        warningAlert(deleteStatus.detail || "Error al eliminar el documento.");
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      warningAlert("Error al eliminar el documento.");
    } finally {
      setDeletingIds((prev) => prev.filter((did) => did !== id));
    }
  };

  const confirmDelete = (id: number) => {
    const document = documents.find((doc) => doc.id === id);
    const message = `¿Está seguro que desea eliminar el documento:\n\n"${document?.name || "Sin nombre"}"?\n\nEsta acción no se puede deshacer.`;
    if (window.confirm(message)) handleDelete(id);
  };

  const handleFileUpload = async (
    file: File,
    documentId: number | undefined,
    documentData: Document,
  ) => {
    try {
      if (!file) throw new Error("No se seleccionó ningún archivo");
      const formData = new FormData();
      if (documentId !== undefined) formData.append("document_id", String(documentId));
      formData.append("file", file);
      appendDocumentUploadFields(formData, file, documentData, companyId);
      const uploadUrl = getAbsoluteUrl("/documents/create_document_url/");
      const uploadResponse = await fetchAuthenticated(uploadUrl, {
        method: "POST",
        body: formData,
      });
      const result = await uploadResponse.json();
      if (!uploadResponse.ok || result.status !== SUCCESS)
        throw new Error(result.detail || "Error al subir el archivo");
      return true;
    } catch (error) {
      console.error("Error en handleFileUpload:", error);
      warningAlert(`Error al subir archivo: ${error instanceof Error ? error.message : String(error)}`);
      return false;
    }
  };

  return { confirmDelete, deletingIds, handleFileUpload, handleSaveChanges };
};

export default useDocumentPersistence;
